// =====================================================================
// PLENARIUM - Tela de entrada do vereador (PLENARIUM:ENTRADA_VEREADOR)
// Usada pelas duas telas de login do vereador:
//  - /login/vereador               (LoginVereador.js)
//  - /camara/:slug/login/vereador  (LoginVereadorSlug.jsx, com o brasao)
// So muda o visual: o login continua sendo o loginVereador(cpf, senha)
// do AuthContext, chamado pela pagina que usa esta tela.
// =====================================================================
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { LogIn, RefreshCw, User, Lock, Eye, EyeOff, Settings, AlertCircle } from 'lucide-react';
import InstalarApp from '../pages/InstalarApp'; // PLENARIUM:INSTALAR_APP

const CSS = `
  .ev-pagina { position: relative; min-height: 100vh; min-height: 100dvh; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 32px 20px 20px; overflow: hidden; background: #f5f8ff; color: #0f172a; font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif; }
  .ev-pagina * { box-sizing: border-box; }
  .ev-fundo { position: absolute; inset: 0; pointer-events: none; z-index: 0; }
  .ev-fundo::before { content: ''; position: absolute; width: 620px; height: 620px; right: -260px; top: -300px; border-radius: 50%; background: radial-gradient(circle at 35% 65%, rgba(59,130,246,.38) 0%, rgba(37,99,235,.16) 45%, rgba(37,99,235,0) 70%); }
  .ev-fundo::after { content: ''; position: absolute; width: 520px; height: 520px; left: -240px; bottom: -260px; border-radius: 50%; background: radial-gradient(circle, rgba(14,165,233,.20) 0%, rgba(14,165,233,0) 68%); }
  .ev-grade { position: absolute; inset: 0; background-image: radial-gradient(rgba(37,99,235,.16) 1px, transparent 1.2px); background-size: 22px 22px; -webkit-mask-image: radial-gradient(ellipse 70% 55% at 50% 35%, #000 0%, transparent 75%); mask-image: radial-gradient(ellipse 70% 55% at 50% 35%, #000 0%, transparent 75%); opacity: .55; }
  .ev-arco { position: absolute; right: -120px; top: -120px; width: 360px; height: 360px; border-radius: 50%; border: 1.5px solid rgba(59,130,246,.22); }
  .ev-arco.dois { right: -60px; top: -60px; width: 240px; height: 240px; border-color: rgba(59,130,246,.16); }
  .ev-caixa { position: relative; z-index: 1; width: 100%; max-width: 420px; animation: evSobe .45s cubic-bezier(.2,.8,.2,1); }
  @keyframes evSobe { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; } }
  @media (min-width: 640px) {
    .ev-caixa { max-width: 460px; background: rgba(255,255,255,.78); -webkit-backdrop-filter: blur(14px); backdrop-filter: blur(14px); border: 1px solid rgba(226,232,240,.9); border-radius: 28px; padding: 40px 40px 32px; box-shadow: 0 30px 70px rgba(15,40,100,.12), 0 2px 6px rgba(15,40,100,.05); }
  }
  .ev-topo { display: flex; flex-direction: column; align-items: center; text-align: center; margin-bottom: 30px; }
  .ev-icone { width: 92px; height: 92px; border-radius: 26px; display: flex; align-items: center; justify-content: center; margin-bottom: 18px; background: linear-gradient(145deg, #eef4ff 0%, #dbe7ff 100%); border: 1px solid rgba(191,212,254,.9); box-shadow: 0 12px 28px rgba(37,99,235,.16), inset 0 1px 0 rgba(255,255,255,.9); }
  .ev-icone.brasao { background: #fff; border-color: #e2e8f0; padding: 10px; }
  .ev-icone.brasao img { max-width: 100%; max-height: 100%; object-fit: contain; }
  .ev-titulo { margin: 0; font-size: 2rem; line-height: 1.15; font-weight: 800; letter-spacing: -.02em; color: #0b1733; }
  .ev-sub { margin: 8px 0 0; font-size: 1.05rem; color: #64748b; }
  .ev-camara { display: inline-flex; align-items: center; gap: 6px; margin-top: 10px; padding: 5px 12px; border-radius: 999px; background: rgba(37,99,235,.08); color: #1d4ed8; font-size: .8rem; font-weight: 700; letter-spacing: .3px; }
  .ev-campo { margin-bottom: 18px; }
  .ev-rotulo { display: block; font-size: .95rem; font-weight: 700; color: #0f172a; margin-bottom: 8px; }
  .ev-entrada { position: relative; display: flex; align-items: center; }
  .ev-entrada > svg { position: absolute; left: 16px; color: #64748b; pointer-events: none; transition: color .15s; }
  .ev-input { width: 100%; height: 56px; border-radius: 14px; border: 1.5px solid #dbe3ef; background: #fff; padding: 0 52px 0 50px; font-size: 1.05rem; font-family: inherit; color: #0f172a; outline: none; transition: border-color .15s, box-shadow .15s; }
  .ev-input::placeholder { color: #94a3b8; }
  .ev-input:focus { border-color: #2563eb; box-shadow: 0 0 0 4px rgba(37,99,235,.14); }
  .ev-entrada:focus-within > svg { color: #2563eb; }
  .ev-olho { position: absolute; right: 8px; width: 40px; height: 40px; border: none; background: none; border-radius: 10px; display: flex; align-items: center; justify-content: center; color: #64748b; cursor: pointer; }
  .ev-olho:hover { background: #f1f5f9; color: #0f172a; }
  .ev-esqueci { display: flex; justify-content: flex-end; margin: -8px 0 22px; }
  .ev-link { background: none; border: none; padding: 0; font-family: inherit; font-size: .9rem; font-weight: 600; color: #2563eb; cursor: pointer; text-decoration: underline; text-underline-offset: 3px; }
  .ev-erro { display: flex; align-items: center; gap: 8px; background: #fef2f2; border: 1px solid #fecaca; color: #b91c1c; border-radius: 12px; padding: 10px 12px; font-size: .9rem; font-weight: 600; margin-bottom: 16px; }
  .ev-botao { width: 100%; height: 58px; border: none; border-radius: 14px; display: flex; align-items: center; justify-content: center; gap: 12px; font-family: inherit; font-size: 1.12rem; font-weight: 700; color: #fff; cursor: pointer; background: linear-gradient(135deg, #2f80ff 0%, #1458e8 100%); box-shadow: 0 12px 26px rgba(20,88,232,.32), inset 0 1px 0 rgba(255,255,255,.25); transition: transform .12s, box-shadow .12s, filter .12s; }
  .ev-botao:hover { filter: brightness(1.05); box-shadow: 0 14px 30px rgba(20,88,232,.38); }
  .ev-botao:active { transform: scale(.985); }
  .ev-botao:disabled { opacity: .75; cursor: wait; }
  .ev-giro { width: 20px; height: 20px; border-radius: 50%; border: 2.5px solid rgba(255,255,255,.45); border-top-color: #fff; animation: evGiro .7s linear infinite; }
  @keyframes evGiro { to { transform: rotate(360deg); } }
  .ev-ou { display: flex; align-items: center; gap: 14px; margin: 26px 0 20px; color: #64748b; font-size: .9rem; }
  .ev-ou::before, .ev-ou::after { content: ''; flex: 1; height: 1px; background: #e2e8f0; }
  .ev-admin { display: flex; align-items: center; justify-content: center; gap: 10px; width: 100%; height: 50px; border-radius: 14px; border: 1.5px solid #e2e8f0; background: rgba(255,255,255,.85); color: #475569; font-size: .98rem; text-decoration: none; transition: border-color .15s, background .15s; }
  .ev-admin:hover { border-color: #bfd4fe; background: #fff; }
  .ev-admin b { color: #2563eb; font-weight: 700; }
  .ev-rodape { position: relative; z-index: 1; display: flex; flex-direction: column; align-items: center; gap: 10px; margin-top: 26px; width: 100%; max-width: 420px; }
  .ev-cache-dica { margin: 0; font-size: .78rem; color: #94a3b8; }
  .ev-cache { display: inline-flex; align-items: center; gap: 8px; height: 40px; padding: 0 18px; border-radius: 12px; border: 1.5px solid #e2e8f0; background: rgba(255,255,255,.7); color: #64748b; font-family: inherit; font-size: .88rem; font-weight: 500; cursor: pointer; }
  .ev-cache:hover { background: #fff; color: #334155; }
  .ev-marca { margin-top: 6px; font-size: .74rem; color: #94a3b8; letter-spacing: .3px; }
  .ev-carregando { position: relative; z-index: 1; display: flex; flex-direction: column; align-items: center; gap: 14px; color: #64748b; }
  .ev-carregando .ev-giro { width: 34px; height: 34px; border-color: rgba(37,99,235,.2); border-top-color: #2563eb; }
  @media (max-width: 639px) {
    .ev-fundo::before { width: 440px; height: 440px; right: -250px; top: -250px; opacity: .65; }
    .ev-arco { right: -140px; top: -140px; width: 300px; height: 300px; }
    .ev-arco.dois { right: -90px; top: -90px; width: 200px; height: 200px; }
  }
  @media (min-width: 640px) and (max-height: 900px) { .ev-pagina { justify-content: flex-start; padding-top: 28px; } .ev-caixa { padding-top: 32px; } .ev-topo { margin-bottom: 24px; } }
  @media (max-width: 380px) { .ev-titulo { font-size: 1.7rem; } .ev-icone { width: 80px; height: 80px; } }
`;

function IconeTribuna() {
  return (
    <svg width="58" height="58" viewBox="0 0 48 48" fill="none" aria-hidden="true">
      <circle cx="22" cy="12" r="5.5" stroke="#1d4ed8" strokeWidth="3" />
      <path d="M11 29c0-6.1 4.9-10.5 11-10.5S33 22.9 33 29" stroke="#1d4ed8" strokeWidth="3" strokeLinecap="round" />
      <path d="M35 15.5c2.6 2.3 2.6 6.7 0 9" stroke="#3b82f6" strokeWidth="2.6" strokeLinecap="round" />
      <path d="M38.6 12.5c4.2 3.9 4.2 11.1 0 15" stroke="#93c5fd" strokeWidth="2.4" strokeLinecap="round" />
      <rect x="6" y="28.5" width="32" height="5.5" rx="2.75" fill="#1d4ed8" />
      <path d="M10.5 36h23l-2.2 8.5H12.7z" fill="#2563eb" />
      <path d="M14 39.5h16" stroke="#93c5fd" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

export function FundoEntrada({ children }) {
  return (
    <div className="ev-pagina">
      <style>{CSS}</style>
      <div className="ev-fundo"><div className="ev-grade" /><div className="ev-arco" /><div className="ev-arco dois" /></div>
      {children}
    </div>
  );
}

export function EntradaCarregando({ texto = 'Carregando...' }) {
  return <FundoEntrada><div className="ev-carregando"><span className="ev-giro" /><span>{texto}</span></div></FundoEntrada>;
}

export default function EntradaVereador({ camara, onEntrar, erro, setErro }) {
  const [cpf, setCpf] = useState('');
  const [senha, setSenha] = useState('');
  const [ver, setVer] = useState(false);
  const [entrando, setEntrando] = useState(false);
  const [limpando, setLimpando] = useState(false);

  const formatCPF = (v) => v.replace(/\D/g, '').slice(0, 11)
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');

  const enviar = async (e) => {
    e.preventDefault();
    if (entrando) return;
    setEntrando(true);
    if (setErro) setErro('');
    try { await onEntrar(cpf, senha); } finally { setEntrando(false); }
  };

  const esqueci = () => toast('Para criar uma nova senha, procure a Secretaria da Câmara.', { icon: '🔑', duration: 5000 });

  const limparCache = async () => {
    setLimpando(true);
    try {
      if ('serviceWorker' in navigator) {
        const registros = await navigator.serviceWorker.getRegistrations();
        for (const reg of registros) await reg.unregister();
      }
      if ('caches' in window) {
        const nomes = await caches.keys();
        await Promise.all(nomes.map(n => caches.delete(n)));
      }
      toast.success('Cache limpo! Recarregando...');
      setTimeout(() => window.location.reload(), 1000);
    } catch (err) {
      toast.error('Erro ao limpar cache');
      setLimpando(false);
    }
  };

  return (
    <FundoEntrada>
      <div className="ev-caixa">
        <div className="ev-topo">
          {camara && camara.logoUrl
            ? <div className="ev-icone brasao"><img src={camara.logoUrl} alt="Brasão" /></div>
            : <div className="ev-icone"><IconeTribuna /></div>}
          <h1 className="ev-titulo">Painel do Vereador</h1>
          <p className="ev-sub">Acesso exclusivo para votação</p>
          {camara && camara.nome && <span className="ev-camara">{camara.nome}</span>}
        </div>

        <form onSubmit={enviar} noValidate={false}>
          <div className="ev-campo">
            <label className="ev-rotulo" htmlFor="ev-cpf">CPF</label>
            <div className="ev-entrada">
              <User size={22} />
              <input id="ev-cpf" className="ev-input" type="text" inputMode="numeric" autoComplete="username"
                value={cpf} onChange={e => setCpf(formatCPF(e.target.value))} placeholder="000.000.000-00" required />
            </div>
          </div>
          <div className="ev-campo">
            <label className="ev-rotulo" htmlFor="ev-senha">Senha</label>
            <div className="ev-entrada">
              <Lock size={22} />
              <input id="ev-senha" className="ev-input" type={ver ? 'text' : 'password'} autoComplete="current-password"
                value={senha} onChange={e => setSenha(e.target.value)} placeholder="••••••••" required />
              <button type="button" className="ev-olho" onClick={() => setVer(v => !v)} aria-label={ver ? 'Esconder senha' : 'Mostrar senha'}>
                {ver ? <EyeOff size={22} /> : <Eye size={22} />}
              </button>
            </div>
          </div>
          <div className="ev-esqueci"><button type="button" className="ev-link" onClick={esqueci}>Esqueci minha senha</button></div>

          {erro && <div className="ev-erro" role="alert"><AlertCircle size={18} style={{ flexShrink: 0 }} />{erro}</div>}

          <button className="ev-botao" type="submit" disabled={entrando}>
            {entrando ? <><span className="ev-giro" /> Entrando...</> : <><LogIn size={24} /> Acessar Painel</>}
          </button>
        </form>

        <div className="ev-ou">ou</div>
        <Link to="/login/admin" className="ev-admin"><Settings size={20} /> Administrador? <b>Acesse aqui</b></Link>

        <InstalarApp />{/* PLENARIUM:INSTALAR_APP */}
      </div>

      <div className="ev-rodape">
        <p className="ev-cache-dica">Tela em branco ou sistema não carrega?</p>
        <button type="button" className="ev-cache" onClick={limparCache} disabled={limpando}>
          <RefreshCw size={16} /> {limpando ? 'Limpando...' : 'Limpar cache e recarregar'}
        </button>
        <span className="ev-marca">Plenarium Digital · desenvolvido por iSolutions</span>
      </div>
    </FundoEntrada>
  );
}
